import {useState} from 'react';
import {Link} from 'react-router';
import {useTranslation} from 'react-i18next';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCommentDots} from '@fortawesome/free-regular-svg-icons';
import {
    CollectionSuggestionTypeEnum,
    SuggestedProblemEditStatusEnum,
    type CollectionSuggestionMinimal,
} from '@types';
import {useSelector} from '@utils';
import {
    SuggestionDeleteButton,
    SuggestionEntry,
    SuggestionTimestamp,
} from './SuggestionEntry';

export const CollectionSuggestionsList = ({
    items,
    showDeleteButton,
    showResolveButton,
    showCollectionColumn,
    showSuggestedByColumn,
    isDeleting,
    isResolving,
    canApprove,
    onDelete,
    onResolve,
}: {
    items: CollectionSuggestionMinimal[];
    showDeleteButton: boolean;
    showResolveButton: boolean;
    showCollectionColumn: boolean;
    showSuggestedByColumn: boolean;
    isDeleting: boolean;
    isResolving: boolean;
    canApprove: (item: CollectionSuggestionMinimal) => boolean;
    onDelete: (id: number) => void;
    onResolve: (
        item: CollectionSuggestionMinimal,
        action: 'approve' | 'reject',
        comment?: string
    ) => Promise<boolean>;
}) => {
    const {t} = useTranslation();
    const {user} = useSelector(state => state.auth);
    const [commentingId, setCommentingId] = useState<number | null>(null);
    const [comment, setComment] = useState('');
    const resolve = async (
        item: CollectionSuggestionMinimal,
        action: 'approve' | 'reject',
        withComment = false
    ) => {
        if (await onResolve(item, action, withComment ? comment : '')) {
            setCommentingId(null);
            setComment('');
        }
    };
    return (
        <ul className="suggestion-entries">
            {items.map(item => {
                const isOpen =
                    item.status === SuggestedProblemEditStatusEnum.OPEN;
                const isAdd = item.type === CollectionSuggestionTypeEnum.ADD;
                const disabledReason = canApprove(item)
                    ? undefined
                    : t(
                          isAdd
                              ? 'collection_suggestions.problem_already_in_collection'
                              : 'collection_suggestions.problem_not_in_collection'
                      );
                return (
                    <SuggestionEntry
                        key={item.id}
                        {...item}
                        metadata={
                            <>
                                {showSuggestedByColumn && (
                                    <span>
                                        {t('common.suggested_by')}{' '}
                                        <Link to={`/users/${item.user.name}`}>
                                            {item.user.name}
                                        </Link>
                                    </span>
                                )}
                                {item.createdAt && (
                                    <span>
                                        {t('suggestions.submitted')}{' '}
                                        <SuggestionTimestamp
                                            date={item.createdAt}
                                        />
                                    </span>
                                )}
                            </>
                        }
                        summary={
                            <>
                                <span
                                    className={`suggestion-change-type ${isAdd ? 'suggestion-change-add' : 'suggestion-change-remove'}`}>
                                    {t(isAdd ? 'common.add' : 'common.remove')}
                                </span>
                                {showCollectionColumn && (
                                    <Link
                                        to={`/collections/${item.collection.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        {item.collection.name}
                                    </Link>
                                )}
                            </>
                        }
                        actions={
                            <>
                                {showResolveButton && isOpen && (
                                    <>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            disabled={
                                                !!disabledReason || isResolving
                                            }
                                            title={disabledReason}
                                            onClick={() =>
                                                void resolve(item, 'approve')
                                            }>
                                            {t('suggestions.approve')}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            disabled={isResolving}
                                            onClick={() =>
                                                void resolve(item, 'reject')
                                            }>
                                            {t('suggestions.reject')}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            disabled={isResolving}
                                            title={t(
                                                'collection_suggestions.resolve_with_comment'
                                            )}
                                            aria-label={t(
                                                'collection_suggestions.resolve_with_comment'
                                            )}
                                            aria-expanded={
                                                commentingId === item.id
                                            }
                                            aria-controls={`suggestion-resolution-${item.id}`}
                                            onClick={() => {
                                                setCommentingId(
                                                    commentingId === item.id
                                                        ? null
                                                        : item.id
                                                );
                                                setComment('');
                                            }}>
                                            <FontAwesomeIcon
                                                icon={faCommentDots}
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </>
                                )}
                                {showDeleteButton &&
                                    isOpen &&
                                    item.user.id === user?.id && (
                                        <SuggestionDeleteButton
                                            disabled={isDeleting || isResolving}
                                            onClick={() => onDelete(item.id)}
                                        />
                                    )}
                            </>
                        }>
                        {commentingId === item.id && isOpen && (
                            <div id={`suggestion-resolution-${item.id}`}>
                                <label
                                    htmlFor={`suggestion-comment-${item.id}`}>
                                    {t(
                                        'collection_suggestions.resolution_comment_optional'
                                    )}
                                </label>
                                <textarea
                                    id={`suggestion-comment-${item.id}`}
                                    className="form-control"
                                    rows={2}
                                    value={comment}
                                    onChange={event =>
                                        setComment(event.target.value)
                                    }
                                />
                                <div className="suggestion-entry-actions">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        disabled={
                                            !!disabledReason || isResolving
                                        }
                                        title={disabledReason}
                                        onClick={() =>
                                            void resolve(item, 'approve', true)
                                        }>
                                        {t(
                                            'collection_suggestions.confirm_approve'
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        disabled={isResolving}
                                        onClick={() =>
                                            void resolve(item, 'reject', true)
                                        }>
                                        {t(
                                            'collection_suggestions.confirm_reject'
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        disabled={isResolving}
                                        onClick={() => {
                                            setCommentingId(null);
                                            setComment('');
                                        }}>
                                        {t('common.cancel')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </SuggestionEntry>
                );
            })}
        </ul>
    );
};
